import React from 'react';
import { Card } from 'antd';

// Shared visual primitives for the Operations screens so the module reads as
// one consistent design (matching the Figma). Colours, stat cards, section
// cards and empty states live here.

// Up-to-two-letter avatar initials from a name. Defensive against non-string
// values and irregular whitespace (double/leading spaces) that would otherwise
// produce "undefined" fragments.
export const initials = (name) =>
  (String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()) || '?';

export const OPS_COLORS = {
  // Deep greens from the User Access Control palette — no light/bright green.
  green: '#067647',
  greenBright: '#15803d',
  orange: '#fa8c16',
  red: '#cf1322',
  blue: '#1890ff',
  purple: '#531dab',
  grey: '#8c8c8c',
  brand: '#0B2A1B',
};

// A headline metric card: label, big value, sub-line, an outline icon top-right
// and an optional coloured left accent bar (as in the Figma dashboard cards).
export const StatCard = ({ label, value, sub, subColor, icon, accent }) => (
  <Card
    bordered={false}
    bodyStyle={{ padding: 20 }}
    style={{
      height: '100%',
      borderRadius: 10,
      borderLeft: `4px solid ${accent || 'transparent'}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: OPS_COLORS.grey, fontSize: 13 }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2, margin: '6px 0' }}>{value}</div>
        <div style={{ color: subColor || OPS_COLORS.grey, fontSize: 13 }}>{sub}</div>
      </div>
      <span style={{ fontSize: 20, color: '#bfbfbf' }}>{icon}</span>
    </div>
  </Card>
);

// A titled content card with the Figma's heavier section heading.
export const SectionCard = ({ title, extra, children, bodyStyle, style, fill }) => (
  <Card
    bordered={false}
    title={<span style={{ fontSize: 18, fontWeight: 700 }}>{title}</span>}
    extra={extra}
    headStyle={{ borderBottom: 'none', paddingTop: 8 }}
    style={{
      borderRadius: 10,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      ...(fill ? { height: '100%' } : {}),
      ...style,
    }}
    bodyStyle={{ paddingTop: 0, ...bodyStyle }}
  >
    {children}
  </Card>
);

export const EmptyBox = ({ text }) => (
  <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '40px 0' }}>{text}</div>
);

// Small round page-header title block ("Operations" / "Dashboard").
export const PageTitle = ({ title, subtitle, right }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: 20,
      flexWrap: 'wrap',
      gap: 12,
    }}
  >
    <div>
      <h1 style={{ margin: 0, fontWeight: 700, fontSize: 26 }}>{title}</h1>
      {subtitle ? <div style={{ color: OPS_COLORS.grey }}>{subtitle}</div> : null}
    </div>
    {right}
  </div>
);
